export const formatBRL = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });

export const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

export const formatMesRef = (yyyymm: string | null | undefined) => {
  if (!yyyymm) return "—";
  const [y, m] = yyyymm.split("-");
  const date = new Date(Number(y), Number(m) - 1, 1);
  return date.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
};

export const formatCPF = (cpf: string | null | undefined) => {
  if (!cpf) return "—";
  const d = cpf.replace(/\D/g, "");
  if (d.length !== 11) return cpf;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
};

export const formatCNPJ = (cnpj: string | null | undefined) => {
  if (!cnpj) return "—";
  const d = cnpj.replace(/\D/g, "");
  if (d.length !== 14) return cnpj;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};

/** Valores da folha vêm da API em centavos; os da NF, em reais. */
export const formatCents = (cents: number) => formatBRL(cents / 100);

/** Centavos -> "1234,56", para preencher inputs de valor. */
export const centsToInput = (cents: number) =>
  (cents / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Até R$ 999.999.999,99 — além disso é quase certamente dígito a mais. */
const MAX_DIGITOS_REAIS = 11;

/**
 * Máscara de valor em tempo real, estilo caixa registradora: só dígitos, os
 * dois últimos são centavos ("850000" -> "8.500,00"). Não há vírgula para o
 * usuário posicionar, então um zero a mais aparece na hora como milhar a mais.
 * A saída é aceita por `parseReaisInput`.
 */
export const maskReaisInput = (input: string): string => {
  const digitos = input.replace(/\D/g, "").replace(/^0+/, "").slice(0, MAX_DIGITOS_REAIS);
  if (!digitos) return "";
  return centsToInput(Number(digitos));
};

/**
 * Texto digitado pelo usuário -> reais como number. LANÇA se não reconhecer.
 *
 * Gramática explícita e ancorada, espelhando `parseValorCents` do backend para
 * que o mesmo texto signifique o mesmo valor na tela e na planilha. O motivo de
 * não usar `Number()` direto: digitar um salário como "8.500" (abreviação
 * natural em pt-BR) seria lido como 8,5 e gravaria R$ 8,50 — e depois voltaria
 * formatado de um jeito plausível, sem ninguém notar.
 *
 * Aceita: 1234 · 1234,56 · 1.234,56 · 1.234.567 · 1234.56 · 1,234.56 ·
 *         R$ 1.234,56 · 1 234,56 · (500,00) e 500- como negativos.
 * Rejeita: 1,234,56 · 1.2345 · texto.
 */
export const parseReaisInput = (input: string): number => {
  let raw = input.replace(/R\$/gi, "").replace(/[\s\u00a0]/g, "");
  if (!raw) return 0;

  let negativo = false;
  if (/^\(.*\)$/.test(raw)) {
    negativo = true;
    raw = raw.slice(1, -1);
  }
  if (raw.startsWith("-")) {
    negativo = !negativo;
    raw = raw.slice(1);
  }
  if (raw.endsWith("-")) {
    negativo = !negativo;
    raw = raw.slice(0, -1);
  }

  let normalizado: string;
  if (/^\d+$/.test(raw)) {
    normalizado = raw;
  } else if (/^\d{1,3}(\.\d{3})+,\d{1,2}$/.test(raw)) {
    normalizado = raw.replace(/\./g, "").replace(",", ".");
  } else if (/^\d{1,3}(,\d{3})+\.\d{1,2}$/.test(raw)) {
    normalizado = raw.replace(/,/g, "");
  } else if (/^\d+,\d{1,2}$/.test(raw)) {
    normalizado = raw.replace(",", ".");
  } else if (/^\d+\.\d{1,2}$/.test(raw)) {
    normalizado = raw;
  } else if (/^\d{1,3}(\.\d{3})+$/.test(raw)) {
    // "8.500" em pt-BR é oito mil e quinhentos, não oito e cinquenta
    normalizado = raw.replace(/\./g, "");
  } else if (/^\d{1,3}(,\d{3})+$/.test(raw)) {
    normalizado = raw.replace(/,/g, "");
  } else {
    throw new Error(`Valor inválido: "${input}"`);
  }

  const n = Number(normalizado);
  if (!Number.isFinite(n)) throw new Error(`Valor inválido: "${input}"`);
  return negativo ? -n : n;
};
